import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  CheckCircle, 
  Circle, 
  Clock, 
  FileCode, 
  Settings, 
  Share2,
  MessageSquare,
  Database,
  Smartphone,
  Figma,
  GitBranch
} from 'lucide-react';
import MainNavbar from '../components/MainNavbar';
import MainFooter from '../components/MainFooter';

// Component for roadmap items
const RoadmapItem = ({ 
  title, 
  description, 
  status, 
  timeframe, 
  icon: Icon 
}) => {
  // Define colors based on status
  const getStatusColor = () => {
    switch(status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800';
      case 'planned':
        return 'bg-yellow-100 text-yellow-800';
      case 'researching':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getStatusIcon = () => {
    switch(status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'in-progress':
        return <Clock className="w-4 h-4" />;
      default:
        return <Circle className="w-4 h-4" />;
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm mb-6 border-l-4 border-orange-500">
      <div className="flex items-start">
        <div className="bg-orange-50 p-3 rounded-lg mr-4 flex-shrink-0">
          <Icon className="h-6 w-6 text-orange-500" />
        </div>
        <div className="flex-grow">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold">{title}</h3>
            <span className={`text-xs px-2 py-1 rounded-full flex items-center ${getStatusColor()}`}>
              {getStatusIcon()}
              <span className="ml-1">{status.charAt(0).toUpperCase() + status.slice(1)}</span>
            </span>
          </div>
          <p className="text-gray-600 mb-3">{description}</p>
          <div className="text-sm text-gray-500 flex items-center">
            <Calendar className="w-4 h-4 mr-1" />
            {timeframe}
          </div>
        </div>
      </div>
    </div>
  );
};

const Roadmap = () => {
  // Roadmap data organized by quarters
  const roadmapData = {
    current: [
      {
        title: "Enhanced Revision Tools",
        description: "Improve the revision interface with more granular controls and the ability to highlight specific areas for revision.",
        status: "in-progress",
        timeframe: "Q2 2025",
        icon: Settings
      },
      {
        title: "Flow Animation Options",
        description: "Add transition effects between views in flows to simulate user interactions and create more realistic prototypes.",
        status: "in-progress",
        timeframe: "Q2 2025",
        icon: GitBranch
      },
      {
        title: "Team Collaboration Features",
        description: "Introduce collaborative editing, comments, and role-based permissions for team members.",
        status: "planned",
        timeframe: "Q2 2025",
        icon: Share2
      }
    ],
    upcoming: [
      {
        title: "Mobile App",
        description: "Native mobile applications for iOS and Android to view and share prototypes on the go.",
        status: "planned",
        timeframe: "Q3 2025",
        icon: Smartphone
      },
      {
        title: "Design System Generation",
        description: "Automatically extract design tokens and create a shareable design system from your prototypes.",
        status: "researching",
        timeframe: "Q3 2025",
        icon: Figma
      },
      {
        title: "Component Library",
        description: "Create, save, and reuse custom components across different projects.",
        status: "planned",
        timeframe: "Q3 2025",
        icon: Database
      },
      {
        title: "API Mocking",
        description: "Create mock API responses to simulate backend integration in your prototypes.",
        status: "researching",
        timeframe: "Q4 2025",
        icon: FileCode
      },
      {
        title: "User Testing Integration",
        description: "Built-in tools for gathering user feedback and analyzing prototype usability.",
        status: "planned",
        timeframe: "Q4 2025",
        icon: MessageSquare
      }
    ],
    completed: [
      {
        title: "Secure Sharing",
        description: "Password-protected sharing and expiration dates for shared links.",
        status: "completed",
        timeframe: "Q1 2025",
        icon: Share2
      },
      {
        title: "HTML Export",
        description: "Export clean, well-structured HTML/CSS code from your prototypes for developer handoff.",
        status: "completed",
        timeframe: "Q1 2025",
        icon: FileCode
      },
      {
        title: "Interactive Flows",
        description: "Connect multiple views to create interactive user journeys and simulate navigation.",
        status: "completed",
        timeframe: "Q1 2025",
        icon: GitBranch
      }
    ]
  };

  return (
    <div className="font-sans antialiased text-gray-900">
      {/* Navbar */}
      <MainNavbar />
      
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">Product Roadmap</h1>
          <p className="text-xl mb-4 max-w-2xl mx-auto">
            Explore our vision for the future of Dessino.ai and see what features we're working on next.
          </p>
        </div>
      </section>
      
      {/* Roadmap Content */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4">
          {/* Current Quarter */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold mb-6 border-b pb-2">Currently in Development</h2>
            {roadmapData.current.map((item, index) => (
              <RoadmapItem 
                key={`current-${index}`}
                title={item.title}
                description={item.description}
                status={item.status}
                timeframe={item.timeframe}
                icon={item.icon}
              />
            ))}
          </div>
          
          {/* Upcoming */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold mb-6 border-b pb-2">Upcoming Features</h2>
            {roadmapData.upcoming.map((item, index) => (
              <RoadmapItem 
                key={`upcoming-${index}`}
                title={item.title}
                description={item.description}
                status={item.status}
                timeframe={item.timeframe}
                icon={item.icon}
              />
            ))}
          </div>
          
          {/* Recently Completed */}
          <div>
            <h2 className="text-3xl font-bold mb-6 border-b pb-2">Recently Completed</h2>
            {roadmapData.completed.map((item, index) => (
              <RoadmapItem 
                key={`completed-${index}`}
                title={item.title}
                description={item.description}
                status={item.status}
                timeframe={item.timeframe}
                icon={item.icon}
              />
            ))}
          </div>
        </div>
      </section>
      
      {/* Feedback Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Have Feature Suggestions?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            We're always looking for ways to improve Dessino.ai. Share your ideas and feedback with us.
          </p>
          <Link to="/contact" className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors inline-block">
            Share Your Feedback
          </Link>
        </div>
      </section>
      
      {/* Footer */}
      <MainFooter />
    </div>
  );
};

export default Roadmap;